import type { ReactNode, RefObject } from "react";

import {
  cardClasses,
  narrowCardClasses,
} from "@/Features/BookShipment/Helpers/bookShipmentStepChrome";

interface BookShipmentChromeLayoutProps {
  /** Which chrome this step of the booking flow gets. */
  chrome: { navigation: boolean; map: boolean; fullScreen: boolean };
  /** The route map, rendered above everything when the step asks for it. */
  map: ReactNode;
  /** The step sidebar, rendered beside the pages on the steps that have one. */
  navigation: ReactNode;
  /** Measures the room the card has, so it can shed its frame when squeezed. */
  cardContainerRef: RefObject<HTMLDivElement | null>;
  cardIsNarrow: boolean;
  /** The step's pages. */
  children: ReactNode;
}

/**
 * The booking flow's page chrome — map, step sidebar, and the card the form
 * sits in. Routing chrome rather than anything a screen composes, and every
 * piece of it is a layout ui-core has no primitive for: the sidebar hides
 * itself below 900px rather than crushing the form column, and the card
 * publishes a `.narrow-cards` class that ui-core's Panel reads (via
 * `in-[.narrow-cards]:`) to drop its own frame in the same breath.
 */
export function BookShipmentChromeLayout({
  chrome,
  map,
  navigation,
  cardContainerRef,
  cardIsNarrow,
  children,
}: BookShipmentChromeLayoutProps) {
  return (
    <>
      {chrome.map && (
        <>
          {map}
          <div className="h-[48px]" />
        </>
      )}

      {chrome.navigation && (
        <div className="flex w-full flex-row items-start">
          {/* The step sidebar is chrome; below ~900px it would crush the
              form column, so it steps aside — Previous/Next still navigate. */}
          <div className="shrink-0 max-[900px]:hidden">{navigation}</div>
          <div className="w-full max-w-[1024px] min-w-0">{children}</div>
        </div>
      )}

      {!chrome.navigation && !chrome.fullScreen && (
        <div
          ref={cardContainerRef}
          className={`flex flex-col flex-nowrap items-center px-4 ${
            cardIsNarrow ? "narrow-cards" : ""
          }`}
        >
          <div className={cardIsNarrow ? narrowCardClasses : cardClasses}>
            {children}
          </div>
        </div>
      )}

      {chrome.fullScreen && (
        <div className="flex min-h-[640px] flex-col flex-nowrap items-center justify-center">
          {children}
        </div>
      )}
    </>
  );
}
